import {Component} from 'preact';
import cx from 'classnames';
import invoke from 'lodash.invoke';
import path from 'ramda/src/path';
import prop from 'ramda/src/prop';
import not from 'ramda/src/not';
import reject from 'ramda/src/reject';
import delay from '../../../../util/fn/delay';
import appLogger from '../../../../modules/core/LoggingProvider';
import {isEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import {Formik} from 'formik';
import {GraphQLErrorDisplay} from '@components/common/GraphQLError/';
import {trimFormData} from '@util/StringUtils'
import {Button, FilterList, FormFeedback, InputFeedback, Message, Radio} from '@ultradent/components';
import AddressBookItem from '@components/account/AddressBook/AddressBookItem';
import AddressFormFields from '@components/account/AddressBook/AddressFormFields';
import PaymentFormFields from '@components/account/Wallet/AddPaymentForm/PaymentFormFields';
import User from '@modules/User';
import PaymetricIframe from '@ultradent/upi-paymetric-iframe';
import TokenService from '@services/TokenService';
import {paymentServiceUnavailable} from '@util/HtmlComponents';
import {CustomerMutations, toAddressInput, toPaymentInput} from '@services/Customer/mutations';
import Icon from '@components/common/Icon';

const initialValues = {
    cardholder: null,
    cardType: null,
    securityCode: null,
    expiresMonth: null,
    expiresYear: null,
    billingAddressId: null,
    address1: null,
    address2: null,
    city: null,
    state: null,
    postalCode: null
};

export class AddPaymentForm extends Component {

    retryDelay = 750;

    constructor ( props ) {
        super( props );

        this.toggleAddAddress = this.toggleAddAddress.bind( this );
        this.submitPaymetricForm = this.submitPaymetricForm.bind( this );
        this.onInvalid = this.onInvalid.bind( this );
        this.onValidate = this.onValidate.bind( this );
        this.onSubmitError = this.onSubmitError.bind( this );
        this.handlePaymetricTokenError = this.handlePaymetricTokenError.bind( this );

        this.state = {
            loading: false,
            errors: {},
            paymetricLoaded: false,
            paymentServiceDownMessage: null,
            isAddingAddress: props.addressBook.length === 0
        };
    }

    hasErrors () {
        return !isEmptyOrNil( this.state.errors );
    }

    getPaymentAccessToken ( retryAttempt = 0 ) {
        return TokenService.getPaymetricAccessToken()
            .catch( err => {
                if ( retryAttempt < 3 ) {
                    const nextAttempt = retryAttempt + 1;
                    const msg = `[request] getPaymetricAccessToken failed, retry attempt (${nextAttempt})`;

                    appLogger.addBreadcrumb( {
                        category: 'request',
                        level: 'warning',
                        message: msg
                    } );
                    return delay( this.retryDelay * nextAttempt )
                        .then( () => this.getPaymentAccessToken( nextAttempt ) );
                }

                throw err;
            } )
    }

    handlePaymetricTokenError ( err ) {
        appLogger.capture( err );

        this.setState( {paymentServiceDownMessage: paymentServiceUnavailable()} );
    }

    componentDidUpdate ( previousProps, previousState, previousContext ) {
        if ( previousProps.addressBook.length !== this.props.addressBook.length ) {
            this.setState( {
                isAddingAddress: this.props.addressBook.length === 0
            } );
        }
    }

    componentDidMount () {
        this.getPaymentAccessToken()
            .then( tokenInfo => {
                if ( !tokenInfo || !tokenInfo.accessToken ) {
                    throw Error( 'PaymetricAccessToken is null or undefiend' );
                }

                this.paymetricToken = tokenInfo.accessToken;

                this.paymetricForm = new PaymetricIframe( {
                    ...tokenInfo,
                    catchTimeout: false
                } );
                return this.paymetricForm.loadIframe( {} )
                    .then( () => this.setState( {paymetricLoaded: true} ) );
            } )
            .catch( this.handlePaymetricTokenError )
    }

    refreshPaymentToken () {
        return this.getPaymentAccessToken()
            .then( tokenInfo => {
                this.paymetricToken = tokenInfo.accessToken;
                if ( this.paymetricForm ) {
                    this.paymetricForm.updateAccessToken( tokenInfo.accessToken );
                }
            } )
    }

    toggleAddAddress () {
        this.setState( {isAddingAddress: !this.state.isAddingAddress} );
    }

    submitPaymetricForm ( {mutation, variables} ) {
        const {isAddingAddress} = this.state;

        this.setState( {
            isSubmitting: true
        } );

        return this.paymetricForm.submitIframe( {
                onInvalidHandler: this.onInvalid,
                onValidate: this.onValidate
            } )
            .then( () => {
                return mutation( {variables} )
                    .then( ( {data} ) => {
                        this.setState( {isSubmitting: false} );
                        const addedPayment = path( ['addPayment', 'addedPayment'] )( data );
                        invoke( this.props, 'onPaymentAdded', addedPayment );

                        if ( isAddingAddress && addedPayment.address ) {
                            invoke( this.props, 'onAddressAdded', addedPayment.address );
                        }
                    } )
            } )
            .catch( this.onSubmitError );
    }

    onInvalid () {
        console.error( '[PaymetricValidationError] Payment validation failed' );
        this.setState( {
            isSubmitting: false
        } );
    }

    onValidate ( isValid ) {
        console.warn( '[PaymetricValidate:isValid]', isValid );
    }

    onSubmitError () {
        console.error( '[PaymetricSubmitError] Payment submission failed' );
        this.refreshPaymentToken().catch( this.handlePaymetricTokenError );
        this.setState( {
            isSubmitting: false
        } );
    }

    render () {
        const {
            addressBook,
            submitButtonLabel = 'Add credit card'
        } = this.props;
        const {isAddingAddress, paymentServiceDownMessage} = this.state;
        const defaultBillingAddress = User.Address.getSuggestedBillingAddress( addressBook );

        if ( paymentServiceDownMessage ) {
            return <div dangerouslySetInnerHTML={{__html: paymentServiceDownMessage}}/>;
        }

        return (
            <CustomerMutations render={( {addPayment} ) =>
                <Formik
                    validate={formData => {
                        const errors = {};
                        const trimmedFormData = trimFormData( formData );
                        const {
                            cardholder,
                            cardType,
                            expiresMonth,
                            expiresYear,
                            securityCode,
                            billingAddressId
                        } = trimmedFormData;

                        errors.cardholder = !cardholder && 'Enter the name listed on the credit card';
                        errors.cardType = !cardType && 'Select a card type';
                        errors.expiresMonth =
                            !expiresMonth && {short: 'Required', long: 'Expiration date (month) is required'};
                        errors.expiresYear =
                            !expiresYear && {short: 'Required', long: 'Expiration date (year) is required'};

                        // check if expiration date is in the past
                        var expiry = new Date( '20' + expiresYear, expiresMonth - 1, 1 );
                        if ( expiry.getTime() < Date.now() ) {
                            errors.expiryDate = 'Expiration date provided has already expired'
                        }

                        if ( !securityCode ) {
                            errors.securityCode =
                                {short: 'Required', long: 'Enter the security code found on credit card'};
                        }
                        if ( securityCode ) {
                            let len = securityCode.toString().length;
                            errors.securityCode = (cardType === 'AMEX')
                                ? len !== 4 && '4 digits Required'
                                : len !== 3 && '3 digits Required';
                        }

                        if ( this.state.isAddingAddress ) {
                            const {address1, city, state, postalCode} = trimmedFormData;
                            errors.address1 = !address1 && 'A street name is required';
                            errors.city = !city && 'A city is required';
                            errors.state = !state && 'State is required';
                            errors.postalCode =
                                !/^\d{5}(-\d{4})?$/.test( postalCode ) && 'Enter a valid US ZIP code';
                            errors.postalCode = !postalCode && 'ZIP code required';
                        }
                        else {
                            errors.billingAddressId = !billingAddressId && 'Select a billing address';
                        }

                        return reject( not, errors );
                    }}
                    onSubmit={values => {
                        const trimmedFormData = trimFormData( values );
                        let postData = {
                            ...trimmedFormData,
                            accessToken: this.paymetricToken,
                            billingAddress: null
                        };

                        // set billingAddress as "AddressInput" if adding new address
                        if ( isAddingAddress ) {
                            postData = {
                                ...postData,
                                billingAddressId: null,
                                billingAddress: toAddressInput( trimmedFormData )
                            }
                        }

                        this.submitPaymetricForm( {
                            mutation: addPayment.mutation,
                            variables: {input: toPaymentInput( postData )}
                        } );
                    }}
                    initialValues={{
                        ...initialValues,
                        billingAddressId: prop( 'id', defaultBillingAddress )
                    }}
                    render={formikBag =>
                        <form onSubmit={formikBag.handleSubmit}>
                            <small>*Indicates a required field</small>
                            <PaymentFormFields {...formikBag} paymetricLoaded={this.state.paymetricLoaded}/>
                            {this.state.isAddingAddress ?
                                <div className="mt-8">
                                    <span className="flex w-full items-center justify-between">
                                        <h2 className="text-lg font-normal flex-0">Enter billing address</h2>
                                        {addressBook.length > 0 &&
                                            <span className="button small ghosted flex-0"
                                                  onClick={this.toggleAddAddress}>Use existing</span>
                                        }
                                    </span>
                                    <AddressFormFields {...formikBag} />
                                </div>
                                :
                                <div className="flex-1 mt-8">
                                    <span className="flex w-full items-center justify-between">
                                        <h2 className="text-lg font-normal flex-0">Select a billing address</h2>
                                        <span className="button small ghosted flex-0"
                                              onClick={this.toggleAddAddress}>Add new</span>
                                    </span>
                                    <FilterList
                                        list={addressBook}
                                        onResultSelect={( evt, result ) =>
                                            formikBag.setFieldValue( 'billingAddressId', result.id )
                                        }
                                        queryProps={['address1', 'address2', 'city', 'state', 'postalCode']}
                                        enableSearchAtCount={9}
                                        maxLength={4}
                                        searchPlaceholder="e.g. 984 Sterling; Dallas TX; 90255"
                                        renderer={( item ) =>
                                            <Radio id={`billingAddress-${item.id}`}
                                                   name="billingAddressId"
                                                   inputClassName="mr-4"
                                                   value={prop( 'id', item )}>
                                                {( {isChecked} ) =>
                                                    <AddressBookItem
                                                        {...item}
                                                        className="px-3 py-2 mb-2 rounded t-bg-color t-duration-short bg-near-white hover:bg-moon-grey cursor-pointe">
                                                        <span className="mr-2">
                                                            <Icon.ui name="checkmark" className={
                                                                cx( 'text-lg', {
                                                                    'text-brand': isChecked,
                                                                    'text-grey-lightest': !isChecked
                                                                } )}
                                                            />
                                                        </span>

                                                    </AddressBookItem>
                                                }
                                            </Radio>
                                        }
                                    />

                                    <InputFeedback
                                        error={formikBag.touched.billingAddressId &&
                                            formikBag.errors.billingAddressId}/>
                                </div>
                            }

                            <GraphQLErrorDisplay
                                error={addPayment.result.error}
                                render={graphQLErrors =>
                                    <Message notice className="my-4">{
                                        graphQLErrors.map( ( {message}, i ) =>
                                            <div className={i > 0 ? 'mt-2' : ''}>{message}</div>
                                        )
                                    }</Message>
                                }/>

                            <FormFeedback {...formikBag} className="bg-fail my-4"/>
                            <div className="flex justify-center mt-8">
                                <Button tabIndex="0" primary fill
                                        type="submit"
                                        loading={this.state.isSubmitting || addPayment.result.loading}
                                        disabled={this.state.isSubmitting}
                                        onClick={formikBag.handleSubmit}>
                                    {submitButtonLabel}
                                </Button>
                            </div>
                        </form>
                    }
                />
            }/>
        );
    }
}

export default AddPaymentForm;
